import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { getStoredAuthToken } from "@/lib/auth-token";
import { formatDateTime } from "@/lib/interview-ui";
import { buildRecordingFileName } from "@/lib/recording-file-name";
import { getApiBaseUrl } from "@/lib/runtime-config";
import { Download } from "lucide-react";
import { useEffect, useState } from "react";

interface RecordingPlayerProps {
  candidateName: string | null;
  interviewId: string;
  contentType: string | null;
  byteSize: number | null;
  durationMs: number | null;
  uploadedAt: string | null;
}

function formatByteSize(byteSize: number | null): string | null {
  if (byteSize === null || byteSize <= 0) {
    return null;
  }
  const megabytes = byteSize / (1024 * 1024);
  return `${megabytes.toFixed(1)} MB`;
}

function formatDuration(durationMs: number | null): string | null {
  if (durationMs === null || durationMs <= 0) {
    return null;
  }
  const totalSeconds = Math.floor(durationMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

export function RecordingPlayer({
  candidateName,
  interviewId,
  contentType,
  byteSize,
  durationMs,
  uploadedAt,
}: RecordingPlayerProps) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    let createdUrl: string | null = null;
    setLoading(true);
    setError(null);
    setObjectUrl(null);

    const load = async () => {
      const token = getStoredAuthToken();
      if (!token) {
        if (active) {
          setError("認証トークンが見つかりません。再度ログインしてください。");
          setLoading(false);
        }
        return;
      }

      try {
        const response = await fetch(
          `${getApiBaseUrl()}/api/v1/admin/interviews/${interviewId}/recording`,
          {
            headers: { authorization: `Bearer ${token}` },
          }
        );
        if (!response.ok) {
          throw new Error("録音の読み込みに失敗しました。");
        }
        const blob = await response.blob();
        if (!active) {
          return;
        }
        createdUrl = URL.createObjectURL(blob);
        setObjectUrl(createdUrl);
      } catch (err) {
        if (active) {
          setError(err instanceof Error ? err.message : "録音の読み込みに失敗しました。");
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    void load();

    return () => {
      active = false;
      if (createdUrl) {
        URL.revokeObjectURL(createdUrl);
      }
    };
  }, [interviewId]);

  function handleDownload() {
    if (!objectUrl) return;
    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    anchor.download = buildRecordingFileName({
      candidateName,
      interviewId,
      uploadedAt,
      contentType,
    });
    document.body.append(anchor);
    anchor.click();
    anchor.remove();
  }

  const sizeLabel = formatByteSize(byteSize);
  const durationLabel = formatDuration(durationMs);
  const metaLabel = [contentType, durationLabel, sizeLabel].filter(Boolean).join(" · ");

  return (
    <Card data-testid="recording-player">
      <CardHeader>
        <CardTitle>面談録音</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading && <p className="text-sm text-muted-foreground">録音を読み込み中...</p>}
        {error && (
          <p data-testid="recording-error" className="text-sm text-destructive">
            {error}
          </p>
        )}
        {objectUrl && (
          // biome-ignore lint/a11y/useMediaCaption: 面談録音には字幕トラックが存在しない
          <audio data-testid="recording-audio" controls src={objectUrl} className="w-full" />
        )}
        {objectUrl && (
          <div className="flex flex-wrap items-center gap-3">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleDownload}
              data-testid="recording-download"
            >
              <Download className="size-4" />
              録音をダウンロード
            </Button>
            {metaLabel.length > 0 && <p className="text-xs text-muted-foreground">{metaLabel}</p>}
          </div>
        )}
        {objectUrl && uploadedAt && (
          <p className="text-xs text-muted-foreground">録音日時: {formatDateTime(uploadedAt)}</p>
        )}
      </CardContent>
    </Card>
  );
}
